import React, { useEffect, useState } from "react";
import { api, formatError } from "../../lib/api";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import { toast } from "sonner";
import { Radio } from "lucide-react";

export function LiveAartiTab() {
  const [cfg, setCfg] = useState({ youtube_video_id: "", title: "Daily Aarti at Pancham Dham", schedule: "6:00 AM & 7:00 PM IST · Daily", offline_message: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/admin/settings/live_aarti").then((r) => {
      if (r.data?.value) setCfg({ ...cfg, ...r.data.value });
    }).catch(() => {});
    // eslint-disable-next-line
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/settings/live_aarti", { value: cfg });
      toast.success("Live Aarti updated — home page refreshed");
    } catch (e) { toast.error(formatError(e)); }
    finally { setSaving(false); }
  };

  return (
    <div data-testid="live-aarti-tab" className="max-w-3xl bg-[#FFFDF9] sacred-border rounded-2xl p-7">
      <div className="flex items-center gap-3 mb-5">
        <Radio className="w-6 h-6 text-[#E65100]" />
        <h3 className="font-serif text-2xl text-[#4A0E17]">Live Aarti Stream</h3>
      </div>
      <p className="text-sm text-[#63534E] mb-5">
        Paste the YouTube video ID of your current live stream (e.g. for <code>https://www.youtube.com/watch?v=<strong>dQw4w9WgXcQ</strong></code>, enter <code>dQw4w9WgXcQ</code>). Leave empty when offline — the tile will show the schedule and a "coming soon" message.
      </p>

      <div className="space-y-4">
        <div>
          <label className="overline">YouTube video ID</label>
          <Input data-testid="aarti-video-id" placeholder="e.g. dQw4w9WgXcQ" value={cfg.youtube_video_id} onChange={(e) => setCfg({ ...cfg, youtube_video_id: e.target.value })} className="mt-2 bg-white border-[#D4AF37]/40 h-11 font-mono" />
        </div>
        <div>
          <label className="overline">Title</label>
          <Input data-testid="aarti-title" placeholder="Shivratri All-Night Jaagran" value={cfg.title} onChange={(e) => setCfg({ ...cfg, title: e.target.value })} className="mt-2 bg-white border-[#D4AF37]/40 h-11" />
        </div>
        <div>
          <label className="overline">Schedule</label>
          <Input data-testid="aarti-schedule" placeholder="6:00 AM & 7:00 PM IST · Daily" value={cfg.schedule} onChange={(e) => setCfg({ ...cfg, schedule: e.target.value })} className="mt-2 bg-white border-[#D4AF37]/40 h-11" />
        </div>
        <div>
          <label className="overline">Offline message</label>
          <Input data-testid="aarti-offline" placeholder="The next aarti stream begins at sunrise." value={cfg.offline_message} onChange={(e) => setCfg({ ...cfg, offline_message: e.target.value })} className="mt-2 bg-white border-[#D4AF37]/40 h-11" />
        </div>
        <Button data-testid="aarti-save" disabled={saving} onClick={save} className="bg-[#4A0E17] hover:bg-[#36080F] rounded-full h-11 px-7">
          {saving ? "Saving…" : "Save stream settings"}
        </Button>
      </div>

      {cfg.youtube_video_id && (
        <div className="mt-6">
          <div className="overline mb-2">Preview</div>
          <div className="aspect-video rounded-xl overflow-hidden sacred-border">
            <iframe
              title="Preview"
              className="w-full h-full"
              src={`https://www.youtube.com/embed/${cfg.youtube_video_id}?rel=0`}
              allowFullScreen
            />
          </div>
        </div>
      )}
    </div>
  );
}

export function CronRunsTab() {
  const [runs, setRuns] = useState([]);
  useEffect(() => { api.get("/admin/cron-runs").then((r) => setRuns(r.data.items)); }, []);
  return (
    <div data-testid="cron-runs-tab" className="bg-[#FFFDF9] sacred-border rounded-2xl overflow-hidden">
      <div className="px-6 py-4 border-b border-[#D4AF37]/30">
        <div className="font-serif text-xl text-[#4A0E17]">Dawn newsletter cron history</div>
        <div className="text-sm text-[#63534E] mt-1">The platform fires <code>POST /api/cron/send-daily-verse</code> at 6:00 AM IST daily.</div>
      </div>
      <table className="w-full text-sm">
        <thead className="bg-[#4A0E17] text-[#F3E5AB]"><tr>
          <th className="text-left px-4 py-3">Started</th>
          <th className="text-left px-4 py-3">Job</th>
          <th className="text-left px-4 py-3">Status</th>
          <th className="text-left px-4 py-3">Verse date</th>
          <th className="text-left px-4 py-3">Sent / Failed</th>
        </tr></thead>
        <tbody>
          {runs.length === 0 && <tr><td colSpan={5} className="px-4 py-10 text-center text-[#63534E]">No cron runs yet. The first will fire at the next 6:00 AM IST.</td></tr>}
          {runs.map((r) => (
            <tr key={r.id} className="border-t border-[#D4AF37]/20">
              <td className="px-4 py-3 font-mono text-xs">{new Date(r.started_at).toLocaleString()}</td>
              <td className="px-4 py-3">{r.job}</td>
              <td className="px-4 py-3"><span className={`px-2 py-0.5 rounded-full text-xs ${r.status === "completed" ? "bg-green-100 text-green-800" : r.status === "errored" ? "bg-red-100 text-red-800" : "bg-amber-100 text-amber-800"}`}>{r.status || "queued"}</span></td>
              <td className="px-4 py-3 font-mono text-xs">{r.verse_date || "—"}</td>
              <td className="px-4 py-3 font-mono">{r.sent ?? "—"} / {r.failed ?? "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
